import "./VideoUI.css"
import { Box } from "@mui/material"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import clsx from "clsx"
import _ from "lodash"
import React, {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react"
import { PlayerEvent, playerStore } from "src/store/PlayerStore"

import { useEventBind } from "../../../hooks/useEventBind"
import { appStore } from "../../../store/AppStore"
import { s } from "../../../utilities/common"
import {
  Buttons,
  dimensions,
  keydown,
  keyup,
} from "../../../utilities/constants"
import { theme, TI } from "../../../utilities/theme"
import { DGText } from "../../common/DGText"
import { Image } from "../../common/Image"
import { ProgressSpinner } from "../../ProgressSpinner"
import { usePlayer } from "../use-player"
import { useProgress } from "../use-progress"

import { PlayerLabel, useTrackIndex } from "./PlayerLabel"
import { ProgressContainer } from "./Sections/ProgressContainer"
import { UISection } from "./Sections/UISection"

export const videoUIWidth = dimensions.width - s(80 * 2) // 80: padding-horizontal

export const VideoUIWrapper = () => {
  const [UIVisible, setUIVisible] = useState(true)
  const subtitleSelectOpen = playerStore((state) => state.subtitleSelectOpen)
  const trackSelectOpen = playerStore((state) => state.trackSelectOpen)
  const streamingQualityOpen = playerStore(
    (state) => state.streamingQualityOpen,
  )
  const prompt = appStore((state) => state.prompt)
  const content = playerStore((state) => state.content)
  const registerBackButtonOverride = appStore(
    (state) => state.registerBackButtonOverride,
  )
  const { open } = playerStore((state) => state.navigationProps)
  const { pause: pausePlayer, play: playPlayer, paused } = usePlayer()
  const pageId = useId()
  const [nextBackPressExit, setNextBackPressExit] = useState(
    content?.__typename === "Album" ||
      content?.__typename === "EditorialPlaylist",
  )
  const suppressPointer = useRef(false)
  const returnFocus = playerStore((state) => state.returnFocus)
  const setContent = playerStore((state) => state.setContent)

  const { changeChapter } = useProgress()

  // the back button is overridden
  // if the content isn't audio, or the UI is currently visible or the user hasn't double back pressed
  const override = useMemo(
    () =>
      (UIVisible || !nextBackPressExit) &&
      content?.__typename !== "Album" &&
      content?.__typename !== "EditorialPlaylist" &&
      !prompt?.open,
    [UIVisible, content, nextBackPressExit, prompt],
  )

  // it's complicated to update all callbacks based on the open prompt values, so we add them here as refs
  const subtitleSelectOpenHelper = useRef<boolean>(null)
  const UIVisibleHelper = useRef<boolean>(null)
  const promptOpenHelper = useRef<boolean>(null)
  const trackSelectOpenHelper = useRef<boolean>(null)
  const streamingQualityOpenHelper = useRef<boolean>(null)
  useEffect(() => {
    subtitleSelectOpenHelper.current = subtitleSelectOpen
    UIVisibleHelper.current = UIVisible
    trackSelectOpenHelper.current = trackSelectOpen
    promptOpenHelper.current = prompt?.open ?? false
    streamingQualityOpenHelper.current = streamingQualityOpen
  }, [
    subtitleSelectOpen,
    UIVisible,
    prompt,
    trackSelectOpen,
    streamingQualityOpen,
  ])

  // show UI after playlist close
  useEffect(() => {
    if (!trackSelectOpen && !streamingQualityOpen) {
      setUIVisible(true)
      hideUITimer()
    }
  }, [trackSelectOpen, streamingQualityOpen])

  const setNavigationProps = playerStore((state) => state.setNavigationProps)

  const closePlayer = useCallback(() => {
    // close player
    pausePlayer()
    setNavigationProps({ open: false })
    if (returnFocus) setFocus(returnFocus)
  }, [pausePlayer, returnFocus, setNavigationProps])

  // This function hides the UI and allows one second longer to pass before the subsequent back button press
  // closes the player
  const hideUI = useCallback(() => {
    if (
      !(
        subtitleSelectOpenHelper.current ||
        promptOpenHelper.current ||
        trackSelectOpenHelper.current ||
        subtitleSelectOpenHelper.current
      )
    )
      setUIVisible(false)
    setTimeout(() => {
      setNextBackPressExit(true)
    }, 1000)
  }, [open])

  // this function sets a countdown to hide the UI timer. If called, the timer resets
  const hideUITimer = useCallback(
    _.debounce(() => {
      // we need to use refs here, because the subscription doesn't update on state changes
      if (
        subtitleSelectOpenHelper.current ||
        trackSelectOpenHelper.current ||
        subtitleSelectOpenHelper.current ||
        content?.__typename === "Album" ||
        content?.__typename === "EditorialPlaylist"
      )
        return
      hideUI()
    }, 3000),
    [content, hideUI],
  )

  const handleInputEvent = useCallback(
    (e: KeyboardEvent) => {
      // the back button press is ignored if any known overlays are drawn over the player.
      if (
        (e.keyCode === Buttons.Back || e.keyCode === Buttons.Stop) &&
        (trackSelectOpenHelper.current ||
          subtitleSelectOpenHelper.current ||
          streamingQualityOpenHelper.current ||
          promptOpenHelper.current)
      ) {
        return
      }
      if (e.keyCode === Buttons.Stop) {
        closePlayer()
        return
      }
      if (e.keyCode === Buttons.Back) {
        // if the back button is currently overridden, set it up so that the subsequent press isn't overridden
        if (override) {
          suppressPointer.current = true
          hideUI()
          setNextBackPressExit(true)
          return
        } else {
          // close player
          closePlayer()
          return
        }
      }
      // media button handling
      // PlayPause before Play or Pause, because it's sometimes the same code
      if ("PlayPause" in Buttons && e.keyCode === Buttons.PlayPause) {
        if (paused) playPlayer()
        else if (!paused) pausePlayer()
      } else if (e.keyCode === Buttons.Play) {
        playPlayer()
      } else if (e.keyCode === Buttons.Pause) {
        pausePlayer()
      } else if (e.keyCode === Buttons.Rewind) {
        changeChapter("left")
      } else if (e.keyCode === Buttons.FF) {
        changeChapter("right")
      }
      // handle mouse move
      if (e.type === "mousemove") {
        // moving the mouse shows the UI after some delay
        setTimeout(() => {
          hideUITimer()
          // if the mouse move is de-prioritized over other actions, ignore it
          if (suppressPointer.current) {
            setTimeout(() => {
              suppressPointer.current = false
            }, 300)
            return
          }
          // if the mouse move is honored after some delay, have it show the UI and reset the back button handling
          setNextBackPressExit(false)
          setUIVisible(true)
        }, 700)
        return
      }
      hideUITimer.cancel()
      setUIVisible(true)
      setNextBackPressExit(false)
    },
    [
      paused,
      pausePlayer,
      changeChapter,
      pausePlayer,
      closePlayer,
      playPlayer,
      override,
      hideUI,
    ],
  )

  const handleKeyUp = useCallback(() => {
    hideUITimer()
  }, [hideUITimer])
  useEventBind(window, keydown, handleInputEvent)
  useEventBind(window, keyup, handleKeyUp)
  useEventBind(window, "mousemove", handleInputEvent)
  useEventBind(window, "click", handleInputEvent)

  useEffect(() => {
    registerBackButtonOverride(pageId, true)
    hideUITimer()
    return () => {
      registerBackButtonOverride(pageId, false)
      setContent(undefined)
      hideUITimer.cancel()
    }
  }, [])

  const uniqueId = useId()
  const setEventListener = playerStore((state) => state.setEventListener)

  useEffect(() => {
    setEventListener(
      PlayerEvent.Loading,
      () => {
        showSpinnerTimer()
      },
      uniqueId,
    )
    setEventListener(
      PlayerEvent.Loaded,
      () => {
        showSpinnerTimer.cancel()
        hideSpinner()
      },
      uniqueId,
    )
    setEventListener(
      PlayerEvent.Progress,
      () => {
        showSpinnerTimer.cancel()
        hideSpinner()
      },
      uniqueId,
    )
  }, [])

  const showSpinnerTimer = useCallback(
    _.debounce(() => {
      const spinner = document.getElementById("player-spinner")
      if (spinner) {
        spinner.style.display = "block"
      }
    }, 1500),
    [],
  )
  const hideSpinner = useCallback(() => {
    const spinner = document.getElementById("player-spinner")
    if (spinner) {
      spinner.style.display = "none"
    }
  }, [])

  return (
    <>
      <Box
        id={"player-spinner"}
        sx={{
          pointerEvents: "none",
          zIndex: 3,
          position: "absolute",
          top: 0,
          left: 0,
          height: "100vh",
          width: "100vw",
        }}
      >
        <ProgressSpinner />
      </Box>
      <VideoUI hidden={!UIVisible} />
    </>
  )
}

const VideoUI = memo(({ hidden = false }: { hidden?: boolean }) => {
  const { focusKey, ref } = useFocusable({
    isFocusBoundary: true,
  })
  const content = playerStore((state) => state.content)
  const poster = playerStore((state) => state.poster)
  const background = playerStore((state) => state.background)
  const [hidePoster, setHidePoster] = useState(false)
  const [hideBackground, setHideBackground] = useState(false)
  const posterPrev = useRef(poster)
  const backgroundPrev = useRef(background)

  /*
   Editorial Playlists have different album covers, and switching the
   track can cause the previous album cover to stay visible until the new one
   loads. we hide it to make sure it is only shown when loaded
   */
  useEffect(() => {
    if (posterPrev.current !== poster) setHidePoster(true)
    if (backgroundPrev.current !== background) setHideBackground(true)
    posterPrev.current = poster
    backgroundPrev.current = background
  }, [poster])

  /*
  The trackId is derived from two states, one from the navigation parameters and
  the other can be inferred by the content query the player does later on.
  The issue is, if we take the trackId from the navigation it will remain static
  even if the track changes, and if we use the one derived by the query, the UI
  will take some time to derive the title. So to keep it synced we have
  to derive it initially from navigation and watch for changes that occur later on
   */
  const { trackId: trackIdNavigation } = playerStore(
    (state) => state.navigationProps,
  )
  const trackIdState = playerStore((state) => state.trackId)
  const [trackId, setTrackId] = useState(trackIdNavigation)

  useEffect(() => {
    setTrackId(trackIdNavigation)
  }, [trackIdNavigation])
  useEffect(() => {
    setTrackId(trackIdState)
  }, [trackIdState])

  const { listIndex, trackIndex } = useTrackIndex(content, trackId)

  if (!content) return null

  return (
    <>
      <Box
        sx={{
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          height: "100vh",
          width: "100vw",
        }}
        className={clsx("videoui", hidden && "hidden")}
      >
        <Box
          sx={{
            zIndex: 2,
            position: "absolute",
            bottom: 0,
            width: "100vw",
            height: s("588px"),
            background:
              "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%)",
          }}
        ></Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <DGText
            tx={TI.Subheadline04tv}
            sx={{
              width: `${videoUIWidth}px`,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: theme.palette.white.main,
              mt: s("80px"),
              textAlign: "left",
              zIndex: 3,
            }}
          >
            {content.__typename !== "EditorialPlaylist"
              ? content.title
              : content.sections[listIndex]?.items[trackIndex]?.album?.title}
          </DGText>
          {poster && (
            <Image
              onLoad={() => setHidePoster(false)}
              sx={{
                zIndex: 3,
                borderRadius: s("12px"),
                width: s("340px"),
                mt: s("32px"),
                alignSelf: "self-start",
              }}
              hasTransparentFallback
              src={!hidePoster ? poster : undefined}
            />
          )}
          {background && (
            <>
              <Image
                onLoad={() => setHideBackground(false)}
                sx={{
                  position: "absolute",
                  zIndex: 1,
                  width: "100vw",
                  top: "calc((100vh - 100vw) / 2)",
                  left: 0,
                }}
                hasTransparentFallback
                src={!hideBackground ? background : undefined}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "rgba(0,0,0,0.4)",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              ></Box>
            </>
          )}
        </Box>
        <Box
          sx={{
            width: `${videoUIWidth}px`,
            zIndex: 2,
            pb: s("84px"),
          }}
        >
          <PlayerLabel />
          <FocusContext.Provider value={focusKey}>
            <Box ref={ref}>
              <ProgressContainer />
              <UISection />
            </Box>
          </FocusContext.Provider>
        </Box>
      </Box>
    </>
  )
})
